import { useMemo } from "react";
import { motion } from "framer-motion";
import { MapPin, Play, X } from "lucide-react";
import { useSliderState } from "./SliderState";
import {
  getShowcaseItems,
  getImageSrc,
  getLowestRate,
  formatPrice,
  getTemplateCurrency,
} from "./templates/utils/showcase";
import Analytics from "../../../../utils/Analytics";
import localization from "../../../../assets/localization";

interface ExploreDrawerProps {
  language: string;
}

type ExploreTile = {
  template: any;
  itemIndex: number;
  title: string;
  image: string | null;
  price: number | null;
  isMap: boolean;
  isVideo?: boolean;
  videoUrl?: string;
  pointCount?: number;
};

type ExploreSection = {
  label: string;
  tiles: ExploreTile[];
};

// The "Esplora" hub: everything the concierge has shown in this session,
// grouped by category instead of stacked chronologically. Replaces the old
// thumbnail carousel.
export default function ExploreDrawer({ language }: ExploreDrawerProps) {
  const sliderState = useSliderState();
  const t = localization.get(language);

  const sections: ExploreSection[] = useMemo(() => {
    const templates = sliderState.templates ?? [];
    const rooms: ExploreTile[] = [];
    const discover: ExploreTile[] = [];
    const places: ExploreTile[] = [];
    const videos: ExploreTile[] = [];

    for (const template of templates) {
      if (template.type === "quote" || template.type === "list_views") {
        const bucket = template.type === "quote" ? rooms : discover;
        getShowcaseItems(template).forEach((item, itemIndex) => {
          bucket.push({
            template,
            itemIndex,
            title: item.title || t.view_photos,
            image: getImageSrc(item.images?.[0] ?? {}) || null,
            price: getLowestRate(item),
            isMap: false,
          });
        });
      } else if (template.type === "videos") {
        (template.templates ?? []).forEach((entry: any, itemIndex: number) => {
          if (!entry?.video_url) return;
          videos.push({
            template,
            itemIndex,
            title: t.video_label.replace("{n}", String(itemIndex + 1)),
            image: null,
            price: null,
            isMap: false,
            isVideo: true,
            videoUrl: entry.video_url,
          });
        });
      } else if (template.type === "map") {
        const points = template.templates ?? [];
        places.push({
          template,
          itemIndex: 0,
          title: points[0]?.name || t.anchor_places,
          image: null,
          price: null,
          isMap: true,
          pointCount: points.length,
        });
      } else if (template.type === "poi_map") {
        const points = template.points ?? [];
        places.push({
          template,
          itemIndex: 0,
          title: template.title || t.anchor_places,
          image: null,
          price: null,
          isMap: true,
          pointCount: points.length,
        });
      } else if (template.type === "itinerary") {
        places.push({
          template,
          itemIndex: 0,
          title: template.title || t.anchor_itinerary,
          image: null,
          price: null,
          isMap: true,
          pointCount: (template.stops ?? []).length,
        });
      }
    }

    return [
      { label: t.explore_rooms, tiles: rooms },
      { label: t.explore_discover, tiles: discover },
      { label: t.explore_videos, tiles: videos },
      { label: t.explore_places, tiles: places },
    ].filter((section) => section.tiles.length > 0);
  }, [sliderState.templates, t]);

  const openTile = (tile: ExploreTile) => {
    Analytics.track("explore_tile_click", {
      template_type: tile.template?.type ?? null,
      item_index: tile.itemIndex,
      item_title: tile.title,
    });
    sliderState.setCurrentTemplate(tile.template, tile.itemIndex);
    sliderState.setExploreOpen(false);
  };

  return (
    <motion.div
      className="explore-drawer"
      initial={{ y: "-104%" }}
      animate={{ y: 0 }}
      exit={{ y: "-104%" }}
      transition={{ type: "spring", stiffness: 320, damping: 34 }}
    >
      <div className="explore-head">
        <span className="explore-title">{t.explore_title}</span>
        <button
          className="explore-close"
          onClick={() => sliderState.setExploreOpen(false)}
          aria-label={t.carousel_close}
        >
          <X className="icon" />
        </button>
      </div>

      <div className="explore-body">
        {sections.map((section) => (
          <div key={section.label} className="explore-section">
            <div className="explore-label">{section.label}</div>
            <div className="explore-grid">
              {section.tiles.map((tile, index) => {
                const isActive =
                  tile.template === sliderState.currentTemplate &&
                  (tile.isMap ||
                    tile.itemIndex === sliderState.subTemplateItemIndex);
                return (
                  <button
                    key={index}
                    className={`explore-tile ${isActive ? "active" : ""} ${
                      tile.isMap ? "map" : ""
                    }`}
                    onClick={() => openTile(tile)}
                  >
                    {tile.isVideo ? (
                      <>
                        <video
                          className="explore-img"
                          src={`${tile.videoUrl}#t=0.001`}
                          muted
                          playsInline
                          preload="metadata"
                        />
                        <span className="explore-pin overlay">
                          <Play className="icon" />
                        </span>
                      </>
                    ) : tile.image ? (
                      <img src={tile.image} alt="" className="explore-img" />
                    ) : (
                      <span className="explore-pin">
                        <MapPin className="icon" />
                      </span>
                    )}
                    <span className="explore-tile-scrim" />
                    <span className="explore-tile-title">{tile.title}</span>
                    {tile.price !== null && (
                      <span className="explore-tile-price">
                        {t.from_price}{" "}
                        {formatPrice(
                          tile.price,
                          language,
                          getTemplateCurrency(tile.template)
                        )}
                      </span>
                    )}
                    {tile.isMap && (tile.pointCount ?? 0) > 1 && (
                      <span className="explore-tile-price">
                        {tile.pointCount} {t.places}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
